import type { Dispatch } from 'react';
import { useReducer, useRef } from 'react';

import {
  createFactsheetTabsState,
  factsheetTabsReducer,
  type FactsheetTabsAction,
  type FactsheetTabsState
} from './factsheetTabsReducer';

export function useFactsheetTabsState(
  factsheetKeys: string
): [FactsheetTabsState, Dispatch<FactsheetTabsAction>] {
  const [state, dispatch] = useReducer(
    factsheetTabsReducer,
    factsheetKeys,
    createFactsheetTabsState
  );
  const prevFactsheetKeysRef = useRef(factsheetKeys);

  // Resets tab state during render when factsheetKeys changes externally
  // (e.g. navigating to a different factsheet URL), avoiding a stale-then-reset
  // render flicker from doing this in an effect. Gated on the prop actually
  // changing (not just disagreeing with state.sourceKeys): a local action like
  // closeTab updates state.sourceKeys immediately while the URL update it
  // triggers is still in flight, so factsheetKeys can lag behind
  // state.sourceKeys for a render or two. Resetting on that transient
  // disagreement would clobber the activeTab/tabNames the action just computed.
  if (factsheetKeys !== prevFactsheetKeysRef.current && factsheetKeys !== state.sourceKeys) {
    dispatch({ type: 'reset', factsheetKeys });
  }
  prevFactsheetKeysRef.current = factsheetKeys;

  return [state, dispatch];
}
